import { useState, type FormEvent, type ReactNode } from "react";
import { can, type Role } from "@sts/schemas";
import type { Invitation, Member, Me, SentInvitation } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { isoDay } from "../lib/format.js";

/**
 * The team screen: who works for this organisation, what they may do, who has
 * been invited and not arrived yet, and — for an owner or an administrator —
 * the controls to invite somebody, change a role, or end somebody's access.
 *
 * WHAT CHANGED HERE, because it is the whole point of the screen. This used to
 * carry an "initial password" box: an administrator typed a colleague's first
 * password, read it out to them, and from then on knew a credential that
 * authenticates as that person — with nothing ever forcing it to change. The
 * surveyor whose name is on a compliance document could not honestly say they
 * were the only person who could have signed it. Now the administrator names
 * the person and their role, and the person chooses a password nobody else ever
 * sees, from a single-use link sent to their own mailbox. The screen says so out
 * loud, because a colleague who is used to being told their password will
 * otherwise ring the office asking for one.
 *
 * The permission matrix is consulted twice, once on each side of the request.
 * The API is the authority: a role holding no `member` grant will be refused
 * by `authorize` inside the service regardless of what this screen renders.
 * The client-side check is only there to hide the buttons that would only ever
 * return 403, so the reader sees the shape of what they may do rather than a
 * form that turns red every time it is submitted.
 */

/** The roles this org can assign, in the same order as the permission matrix. */
const ROLE_OPTIONS: readonly Role[] = [
  "owner",
  "admin",
  "office",
  "surveyor",
  "designer",
  "technical-supervisor",
  "installer",
  "auditor",
];

/**
 * How each role reads on screen. The scheme's own vocabulary rather than
 * the enum's dashes — a surveyor never writes "technical-supervisor" on a form,
 * and a screen that does is asking them to translate.
 */
const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  admin: "Administrator",
  office: "Office / Sales",
  surveyor: "Surveyor",
  designer: "Designer",
  "technical-supervisor": "Technical supervisor",
  installer: "Installer",
  auditor: "Auditor (read only)",
};

/**
 * What ending somebody's access actually does now, in one sentence, kept in one
 * place so the confirmation and the section note cannot drift apart.
 *
 * Every word of it is load-bearing. Deactivating used to leave the token on a
 * dismissed surveyor's phone working for another month; it now ends their
 * access as the call returns. An administrator who thinks this is a tidy-up for
 * later, and finds they have cut a colleague off mid-visit, learned the hard
 * way what the screen should have told them.
 *
 * "Access to THIS organisation", not "signed out", and the distinction is real
 * rather than pedantic: somebody employed by two installers keeps the other
 * employer's sessions — the API revokes per membership and refuses this one at
 * every request instead. Saying "signed out everywhere" would be a promise the
 * system deliberately does not keep, and the one person it is wrong about is
 * the subcontractor working for two firms at once.
 */
const DEACTIVATION_EFFECT =
  "Their access to this organisation ends everywhere straight away, including " +
  "on their phone — anything already captured on it stays on the phone, but it " +
  "cannot be sent while their access is off.";

interface Team {
  readonly me: Me;
  readonly members: readonly Member[];
  /** Empty for a role that may read members but not manage them. */
  readonly invitations: readonly Invitation[];
  readonly mayManage: boolean;
}

export function TeamScreen(): ReactNode {
  const { client } = usePortal();

  const { state, reload } = useAsync<Team>(async () => {
    const me = await client.me();
    // Consulted before the list so a refusal here is the one this screen
    // renders, rather than one buried inside `Async`'s failure branch.
    const mayRead = can({ role: me.role, resource: "member", action: "read" });
    if (!mayRead) {
      return { me, members: [], invitations: [], mayManage: false };
    }
    const members = await client.organisationMembers();
    const mayManage = can({
      role: me.role,
      resource: "member",
      action: "create",
    });
    // Only asked for when it can be acted on. The invitation list is gated on
    // `member:read` server-side, so a reader could load it — but a list of
    // pending invitations with no Resend and no Withdraw is a list of things
    // to go and ask somebody else about.
    const invitations = mayManage ? await client.organisationInvitations() : [];
    return { me, members, invitations, mayManage };
  }, [client]);

  return (
    <>
      <div className="page-header">
        <h1>Team</h1>
        <p className="lede">
          Everybody in this organisation and the login they sign in with. Only
          an owner or an administrator may invite somebody or change a role.
        </p>
      </div>

      <Async state={state} what="the team" reload={reload}>
        {(team) =>
          can({ role: team.me.role, resource: "member", action: "read" }) ? (
            <TeamView team={team} reload={reload} />
          ) : (
            <div className="notice warn" role="alert">
              <p>
                Your role may not <strong>read</strong> <strong>member</strong>,
                so the team roster is not shown. This is a permission, not a
                fault: ask an owner or an administrator.
              </p>
            </div>
          )
        }
      </Async>
    </>
  );
}

function TeamView({
  team,
  reload,
}: {
  readonly team: Team;
  readonly reload: () => void;
}): ReactNode {
  return (
    <>
      <section>
        <h2>
          Members <span className="count">{team.members.length}</span>
        </h2>
        {team.members.length === 0 ? (
          <p className="empty">Nobody is a member here yet.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">
                Members of this organisation
              </caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Joined</th>
                  {team.mayManage ? <th scope="col">Manage</th> : null}
                </tr>
              </thead>
              <tbody>
                {team.members.map((member) => (
                  <MemberRow
                    key={member.userId}
                    member={member}
                    mayManage={team.mayManage}
                    isSelf={member.userId === team.me.userId}
                    reload={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {team.mayManage ? (
          <p className="hint small">
            Ending somebody's access does not delete them: their name stays on
            the work they did. {DEACTIVATION_EFFECT}
          </p>
        ) : null}
      </section>

      {team.mayManage ? (
        <>
          <PendingInvitations invitations={team.invitations} reload={reload} />
          <InviteMember reload={reload} />
        </>
      ) : (
        <div className="notice" role="note">
          <p className="small">
            Your role may read the team but not change it, so the invitation
            form and the manage controls are not shown.
          </p>
        </div>
      )}
    </>
  );
}

function MemberRow({
  member,
  mayManage,
  isSelf,
  reload,
}: {
  readonly member: Member;
  readonly mayManage: boolean;
  readonly isSelf: boolean;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [role, setRole] = useState<Role>(member.role);
  const [editing, setEditing] = useState(false);
  /**
   * Deactivation is confirmed, reactivation is not.
   *
   * Not symmetry for its own sake: one of the two ends somebody's working day
   * on a doorstep, and the other gives it back. The confirmation exists to put
   * `DEACTIVATION_EFFECT` in front of the person doing it at the moment they
   * do it, rather than in a note above the table they read once in March.
   */
  const [confirming, setConfirming] = useState(false);

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submit(async () => {
      await client.updateOrganisationMember(member.userId, { role });
      setEditing(false);
      reload();
    });
  }

  async function onSetActive(active: boolean): Promise<void> {
    await submit(async () => {
      await client.updateOrganisationMember(member.userId, { active });
      setConfirming(false);
      reload();
    });
  }

  return (
    <tr>
      <th scope="row">
        {member.fullName}
        {isSelf ? <span className="muted small"> · you</span> : null}
      </th>
      <td className="mono small">{member.email}</td>
      <td>
        {editing ? (
          <select
            aria-label={`Role for ${member.fullName}`}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABEL[option]}
              </option>
            ))}
          </select>
        ) : (
          ROLE_LABEL[member.role]
        )}
      </td>
      <td>
        {member.active ? (
          "Active"
        ) : (
          <span className="muted">Access ended</span>
        )}
      </td>
      <td className="ts">{isoDay(member.createdAt)}</td>
      {mayManage ? (
        <td className="small">
          {editing ? (
            <form onSubmit={(e) => void onSave(e)} className="inline">
              <button type="submit" disabled={busy || role === member.role}>
                {busy ? "Saving…" : "Save role"}
              </button>{" "}
              <button
                type="button"
                className="link"
                onClick={() => {
                  setRole(member.role);
                  setEditing(false);
                }}
              >
                Cancel
              </button>
            </form>
          ) : confirming ? (
            <div role="group" aria-label={`End ${member.fullName}'s access`}>
              <p className="small">{DEACTIVATION_EFFECT}</p>
              <button
                type="button"
                onClick={() => void onSetActive(false)}
                disabled={busy}
              >
                {busy ? "Ending…" : `End ${member.fullName}'s access`}
              </button>{" "}
              <button
                type="button"
                className="link"
                onClick={() => setConfirming(false)}
              >
                Keep their access
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="link"
                onClick={() => setEditing(true)}
              >
                Change role
              </button>{" "}
              {member.active ? (
                <button
                  type="button"
                  className="link"
                  onClick={() => setConfirming(true)}
                  disabled={busy}
                >
                  End access
                </button>
              ) : (
                <button
                  type="button"
                  className="link"
                  onClick={() => void onSetActive(true)}
                  disabled={busy}
                >
                  Restore access
                </button>
              )}
            </>
          )}
          {error !== null ? (
            <Failure error={error} what="that change" />
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

/**
 * The people who have been invited and have not arrived yet.
 *
 * Listed at all because "I invited Bob last week and he says he never got it"
 * is otherwise unanswerable: without this, an administrator cannot tell an
 * invitation that is sitting unread from one that was never sent. Expiry is
 * shown for the same reason — an invitation that quietly lapsed looks exactly
 * like one still waiting.
 */
function PendingInvitations({
  invitations,
  reload,
}: {
  readonly invitations: readonly Invitation[];
  readonly reload: () => void;
}): ReactNode {
  return (
    <section>
      <h2>
        Invited, not joined yet{" "}
        <span className="count">{invitations.length}</span>
      </h2>
      {invitations.length === 0 ? (
        <p className="empty">Nobody is waiting on an invitation.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <caption className="visually-hidden">
              Invitations that have not been accepted
            </caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Invited by</th>
                <th scope="col">Link expires</th>
                <th scope="col">Manage</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((invitation) => (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  reload={reload}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Whether the link on this invitation has lapsed.
 *
 * The API renders `expires_at` as a Postgres timestamp string rather than
 * strict ISO 8601 ("2026-09-22 10:00:00+00"), which `Date.parse` does read —
 * but an unparseable value is treated as not expired rather than rendering
 * every invitation dead, because "expired" is the claim that makes somebody
 * send a second one.
 */
function hasExpired(expiresAt: string, now = Date.now()): boolean {
  const at = Date.parse(expiresAt);
  return !Number.isNaN(at) && at <= now;
}

function InvitationRow({
  invitation,
  reload,
}: {
  readonly invitation: Invitation;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [delivery, setDelivery] = useState<SentInvitation | null>(null);
  const [confirming, setConfirming] = useState(false);
  const expired = hasExpired(invitation.expiresAt);

  async function onResend(): Promise<void> {
    await submit(async () => {
      // A resend rotates the token, so the previous link stops working. That
      // is the point — it is also how an administrator pulls back a link that
      // went to the wrong mailbox — and the note below says so.
      setDelivery(await client.resendInvitation(invitation.id));
      reload();
    });
  }

  async function onRevoke(): Promise<void> {
    await submit(async () => {
      await client.revokeInvitation(invitation.id);
      setConfirming(false);
      reload();
    });
  }

  return (
    <tr>
      <th scope="row">{invitation.fullName}</th>
      <td className="mono small">{invitation.email}</td>
      <td>{ROLE_LABEL[invitation.role]}</td>
      <td className="small">{invitation.invitedByName ?? "—"}</td>
      <td className="ts">
        {isoDay(invitation.expiresAt)}
        {expired ? (
          <>
            {" "}
            <span className="muted small">expired</span>
          </>
        ) : null}
        {invitation.sendCount > 1 ? (
          <div className="muted small">sent {invitation.sendCount} times</div>
        ) : null}
      </td>
      <td className="small">
        {confirming ? (
          <div
            role="group"
            aria-label={`Withdraw the invitation to ${invitation.email}`}
          >
            <p className="small">
              The link stops working immediately and cannot be brought back. To
              invite them later, send a new invitation.
            </p>
            <button type="button" onClick={() => void onRevoke()} disabled={busy}>
              {busy ? "Withdrawing…" : "Withdraw invitation"}
            </button>{" "}
            <button
              type="button"
              className="link"
              onClick={() => setConfirming(false)}
            >
              Keep it
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="link"
              onClick={() => void onResend()}
              disabled={busy}
            >
              {busy ? "Sending…" : expired ? "Send a new link" : "Resend"}
            </button>{" "}
            <button
              type="button"
              className="link"
              onClick={() => setConfirming(true)}
              disabled={busy}
            >
              Withdraw
            </button>
          </>
        )}
        {delivery ? (
          <DeliveryNote
            sent={delivery}
            what={`A fresh link is on its way to ${invitation.email}. The previous link no longer works.`}
          />
        ) : null}
        {error !== null ? (
          <Failure error={error} what="that invitation" />
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Whether an email really left, said plainly either way.
 *
 * The API reports `delivered: false` with a reason when the deployment sends no
 * mail at all — `EMAIL_TRANSPORT=console` throws every message away by design —
 * and the invitation is still perfectly real and acceptable. Showing "invitation
 * sent" over that is how somebody waits a week for an email that was never
 * going to arrive.
 */
function DeliveryNote({
  sent,
  what,
}: {
  readonly sent: SentInvitation;
  readonly what: string;
}): ReactNode {
  if (!sent.delivery.delivered) {
    return (
      <div className="notice warn" role="status">
        <p className="small">
          {sent.invitation.email} was invited, but no email was sent.{" "}
          {sent.delivery.note ?? ""}
        </p>
      </div>
    );
  }
  return (
    <div className="notice" role="status">
      <p className="small">{what}</p>
    </div>
  );
}

function InviteMember({ reload }: { readonly reload: () => void }): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("surveyor");
  const [sent, setSent] = useState<SentInvitation | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submit(async () => {
      const result = await client.inviteMember({
        fullName: fullName.trim(),
        email: email.trim(),
        role,
      });
      setSent(result);
      setFullName("");
      setEmail("");
      setRole("surveyor");
      reload();
    });
  }

  return (
    <section>
      <h3>Invite somebody</h3>
      <p className="hint small">
        We email them a link that works once. They choose their own password —
        nobody here sees it, not you and not anybody else in the office. The
        link expires after seven days; you can send a fresh one at any time.
      </p>
      <form className="stacked" onSubmit={(e) => void onSubmit(e)}>
        <div className="field">
          <label htmlFor="member-name">Full name</label>
          <input
            id="member-name"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="member-email">Email</label>
          <input
            id="member-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className="hint small">
            Their own work address — the invitation goes there, and it is the
            address they will sign in with.
          </p>
        </div>
        <div className="field">
          <label htmlFor="member-role">Role</label>
          <select
            id="member-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABEL[option]}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={busy || fullName.trim() === "" || email.trim() === ""}
        >
          {busy ? "Sending…" : "Send invitation"}
        </button>
      </form>
      {sent ? (
        <DeliveryNote
          sent={sent}
          what={`Invitation sent to ${sent.invitation.email}. They appear in the team list once they have chosen a password and signed in.`}
        />
      ) : null}
      {error !== null ? <Failure error={error} what="that invitation" /> : null}
    </section>
  );
}
